// utils/saveCampaign.js
// NEW: Import specific API modules instead of generic api

import { campaignAPI, facilityAPI, squadAPI, researchAPI } from '../api';

/**
 * Saves campaign progress to the backend
 * @param {Object} campaign - The current campaign object
 * @param {Object} resources - Current resource state
 * @returns {Promise<boolean>} - Success status
 * 
 * @example
 * const success = await saveCampaignProgress(campaign, { supplies: 100, intel: 50, funds: 200 });
 */
export const saveCampaignProgress = async (campaign, resources) => {
  if (!campaign?.id) {
    console.warn('No campaign ID provided for save');
    return false;
  }

  try {
    await campaignAPI.updateCampaign(campaign.id, {
      day: campaign.day ?? 0,
      threat_level: campaign.threat_level ?? 0,
      supplies: resources.supplies ?? 0,
      funds: resources.funds ?? 0,
      intel: resources.intel ?? 0,
      reputation: campaign.reputation ?? 0,
      total_playtime: campaign.total_playtime ?? 0,
      is_active: campaign.is_active ?? 1,
      game_over: campaign.game_over ?? 0,
      victory: campaign.victory ?? 0,
      last_played: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error saving campaign progress:', error);
    return false;
  }
};

/**
 * Saves complete game state including campaign, facilities, squad, and research
 * @param {Object} gameState - Complete game state object
 * @param {Object} gameState.campaign - Campaign data
 * @param {Object} gameState.resources - Resource data (supplies, funds, intel)
 * @param {Array} gameState.facilities - Array of facility objects
 * @param {Array} gameState.squad - Array of squad member objects
 * @param {Array} gameState.research - Array of research objects
 * @param {Object} gameState.currentResearch - Current active research object
 * @returns {Promise<Object>} Status object with success flags and errors
 * 
 * @example
 * const result = await saveCompleteGameState({
 *   campaign: campaignData,
 *   resources: { supplies: 100, funds: 200, intel: 50 },
 *   facilities: facilitiesArray,
 *   squad: squadArray,
 *   research: researchArray,
 *   currentResearch: activeResearchObject
 * });
 * // Returns: { campaign: true, facilities: true, squad: true, research: true, errors: [] }
 */
export const saveCompleteGameState = async (gameState) => {
  const { campaign, resources, facilities = [], squad = [], research = [], currentResearch = null } = gameState;
  
  const status = {
    campaign: false,
    facilities: false,
    squad: false,
    research: false,
    errors: []
  };

  // Save campaign progress
  try {
    status.campaign = await saveCampaignProgress(campaign, resources);
    if (!status.campaign) {
      status.errors.push('Campaign save failed');
    }
  } catch (error) {
    console.error('Error saving campaign:', error);
    status.errors.push(`Campaign: ${error.message}`);
  }

  // Save facilities (only dirty items)
  try {
    const dirtyFacilities = facilities.filter(f => f.isDirty);
    if (dirtyFacilities.length > 0) {
      const facilityUpdates = dirtyFacilities.map(f => ({
        facilityId: f.id,
        updates: f
      }));
      await facilityAPI.batchUpdateFacilities(facilityUpdates);
      clearDirtyFlags(facilities);
      status.facilities = true;
    } else {
      status.facilities = true; // No changes needed
    }
  } catch (error) {
    console.error('Error saving facilities:', error);
    status.errors.push(`Facilities: ${error.message}`);
  }

  // Save squad members (only dirty items)
  try {
    const dirtySquad = squad.filter(s => s.isDirty);
    if (dirtySquad.length > 0) {
      const squadUpdates = dirtySquad.map(s => ({
        memberId: s.id,
        updates: s
      }));
      await squadAPI.batchUpdateSquad(squadUpdates);
      clearDirtyFlags(squad);
      status.squad = true;
    } else {
      status.squad = true; // No changes needed
    }
  } catch (error) {
    console.error('Error saving squad:', error);
    status.errors.push(`Squad: ${error.message}`);
  }

  // Save research progress (only dirty items)
  try {
    // Collect all research items that need saving
    const allResearch = [...research];
    
    // Add current active research if it exists and isn't already in the list
    if (currentResearch && currentResearch.id) {
      const existsInList = allResearch.some(r => r.id === currentResearch.id);
      if (!existsInList) {
        allResearch.push(currentResearch);
      }
    }
    
    const dirtyResearch = allResearch.filter(r => r.isDirty && r.id);
    
    if (dirtyResearch.length > 0) {
      // Save each dirty research item
      for (const item of dirtyResearch) {
        const updates = {
          progress: item.progress,
          completed: item.completed ?? 0,
          ...(item.completed_at && { completed_at: item.completed_at })
        };
        
        await researchAPI.updateResearch(item.id, updates);
      }
      
      clearDirtyFlags(allResearch);
      status.research = true;
    } else {
      status.research = true; // No changes needed
    }
  } catch (error) {
    console.error('Error saving research:', error);
    status.errors.push(`Research: ${error.message}`);
  }

  return status;
};

/**
 * Marks specific items as dirty (needing save) by their IDs
 * @param {Array} items - Array of objects to mark
 * @param {Array<number|string>} ids - Array of IDs to mark as dirty
 * @returns {number} Number of items marked as dirty
 * 
 * @example
 * markAsDirty(facilities, [1, 3, 5]); // Marks facilities with IDs 1, 3, and 5
 */
export const markAsDirty = (items, ids) => {
  if (!Array.isArray(items) || !Array.isArray(ids)) {
    console.warn('markAsDirty requires array parameters');
    return 0;
  }

  const idSet = new Set(ids);
  let count = 0;

  items.forEach(item => {
    if (item && idSet.has(item.id)) {
      item.isDirty = true;
      count++;
    }
  });

  return count;
};

/**
 * Clears isDirty flags from all items in an array
 * @param {Array} items - Array of objects with isDirty flags
 * @returns {number} Number of flags cleared
 * 
 * @example
 * clearDirtyFlags(facilities); // Removes isDirty from all facilities
 */
export const clearDirtyFlags = (items) => {
  if (!Array.isArray(items)) {
    console.warn('clearDirtyFlags requires an array parameter');
    return 0;
  }

  let count = 0;

  items.forEach(item => {
    if (item && item.isDirty) {
      delete item.isDirty;
      count++;
    }
  });

  return count;
};

/**
 * Creates an auto-save interval for campaign progress
 * @param {Object} campaign - The current campaign object
 * @param {Object} resources - Current resource state
 * @param {number} intervalMs - Save interval in milliseconds (default: 30000)
 * @returns {number} - Interval ID for cleanup
 * 
 * @example
 * const intervalId = createAutoSave(campaign, resources, 60000); // Auto-save every minute
 * // Later: clearAutoSave(intervalId);
 */
export const createAutoSave = (campaign, resources, intervalMs = 30000) => {
  return setInterval(() => {
    saveCampaignProgress(campaign, resources);
  }, intervalMs);
};

/**
 * Clears an auto-save interval
 * @param {number} intervalId - The interval ID to clear
 * 
 * @example
 * clearAutoSave(intervalId);
 */
export const clearAutoSave = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};